import { XsI18nPipe } from '@nx-fe/pipes';
import { IDeviceModel, IMCConnectionInfo, XDeviceService, isDefined } from '@nx-fe/services';
import {
  ChatRoomHistoryEntity,
  Room,
  SabhaCall,
  SabhaCallService,
  SabhaChatNotificationService,
  SabhaChatService,
  SabhaUserEntity,
  UserRepository,
} from '@soti/sabha-components';
import { BehaviorSubject, Observable, Subscription, combineLatest, interval, of } from 'rxjs';
import { filter, first, map, shareReplay, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { getMcInstanceCaption } from '../../sabha-utils';
import { SabhaService } from '../../sabha.service';
import { TIMEOUT_PROMPT_BEFORE } from '../../services/xsight-room.service';

export class XSightRoom {
  public get id() {
    return this._sabhaRoom.id;
  }

  public get ownerId(): string {
    return this._sabhaRoom.ownerId;
  }

  public get name(): string {
    return this._sabhaRoom.name;
  }

  public get creationTime(): Date {
    return this._sabhaRoom.creationTime;
  }

  public get activityTime(): Date {
    return new Date(this._sabhaRoom.activityTime);
  }

  public get activityTime$(): Observable<number> {
    return this._sabhaRoom$.pipe(map(room => new Date(room.activityTime).getTime()));
  }

  public get deletionTime$(): Observable<number> {
    return this._sabhaRoom$.pipe(
      map(room => {
        if (!isDefined(room.expirationTime)) {
          room.expirationTime = Date.now() + new Date(TIMEOUT_PROMPT_BEFORE).getTime();
        }

        return room.expirationTime;
      })
    );
  }

  public get participants() {
    return this._sabhaRoom.participants;
  }

  public readonly isRcing$ = new BehaviorSubject<boolean>(false);
  public readonly isRinging$ = new BehaviorSubject<boolean>(false);

  // statusId: number;
  // type: SabhaRoomType;
  // configurations: SabhaRoomConfigurations;
  public readonly callContext$ = new BehaviorSubject<SabhaCall | null>(null);

  public get callDuration$(): Observable<number> {
    if (!this._callDuration$) {
      this.setupCallDuration();
    }

    return this._callDuration$.asObservable();
  }

  public get callContext(): SabhaCall {
    return this.callContext$.value;
  }

  public get isRcing(): boolean {
    return this.isRcing$.value;
  }

  public get isRinging(): boolean {
    return this.isRinging$.value;
  }

  public set sabhaRoom(sabhaRoom: Room) {
    this._sabhaRoom = sabhaRoom;
    this._sabhaRoom$.next(sabhaRoom);
  }

  public canCall$: Observable<boolean>;

  public readonly chatContext$ = new BehaviorSubject<ChatRoomHistoryEntity | null>(null);

  public readonly chatNotificationCount$: Observable<number>;

  public readonly lastUpdatedDate$: Observable<number>;

  public readonly mcInstanceName$: Observable<string>;
  private _subscriptions = new Subscription();
  private _callDuration$: BehaviorSubject<number>;
  private _sabhaRoom$ = new BehaviorSubject<Room>(this._sabhaRoom);
  private _callDurationSubscription = new Subscription();

  constructor(
    private _sabhaRoom: Room,
    private userRepository: UserRepository,
    chatService: SabhaChatService,
    chatNotificationService: SabhaChatNotificationService,
    callService: SabhaCallService,
    sabhaService: SabhaService,
    deviceService: XDeviceService,
    xsI18nPipe: XsI18nPipe,
    rcDevice$: Observable<IDeviceModel>,
    ringIds$: Observable<string[]>,
    mcConnections$: Observable<IMCConnectionInfo[]>,
  ) {
    this._subscriptions.add(
      callService.activeCalls$.subscribe(activeCalls => {
        const roomCall = activeCalls.find(call => call.roomId === this.id);
        this.callContext$.next(roomCall);
      })
    );

    this._subscriptions.add(
      chatService.userHistoryUpdated$.subscribe({
        next: chatRoomHistories => {
          const roomChat = chatRoomHistories.find(chat => chat.roomId === this.id);
          this.chatContext$.next(roomChat as ChatRoomHistoryEntity);
        },
      })
    );

    this._subscriptions.add(
      combineLatest([rcDevice$, this.getSabhaDeviceParticipant$()]).subscribe(
        ([device, participant]: [IDeviceModel, SabhaUserEntity]) => {
          if (device && participant) {
            const isRcing = device.mcDeviceId === participant.externalId;
            if (isRcing !== this.isRcing) {
              this.isRcing$.next(isRcing);
            }
          } else if (this.isRcing === true) {
            this.isRcing$.next(false);
          }
        }
      )
    );

    this._subscriptions.add(
      ringIds$.subscribe(ringIds => {
        if (ringIds.includes(this.id)) {
          if (!this.isRinging) {
            this.isRinging$.next(true);
          }
        } else if (this.isRinging === true) {
          this.isRinging$.next(false);
        }
      })
    );

    this._subscriptions.add(
      this.callContext$
        .pipe(
          filter(c => !!c),
          switchMap(c => c.onDestroy$)
        )
        .subscribe(_ => {
          this.onCallEnd();
        })
    );

    this.canCall$ = this.getRemoteParticipants$().pipe(
      filter((remoteParticipants: SabhaUserEntity[]) => !!remoteParticipants[0]),
      switchMap((remoteParticipants: SabhaUserEntity[]) => {
        return sabhaService.canCallParticipant$(remoteParticipants[0].userId);
      })
    );

    this.mcInstanceName$ = this.getSabhaDeviceParticipant$().pipe(
      map((participant: SabhaUserEntity) => participant.externalId),
      switchMap((deviceId: string) => deviceService.getDevice(deviceId)),
      withLatestFrom(mcConnections$),
      map(([device, mcConnections]: [IDeviceModel, IMCConnectionInfo[]]) =>
        getMcInstanceCaption(device, mcConnections, xsI18nPipe)
      ),
      shareReplay(1)
    );

    this.lastUpdatedDate$ = this.getLastUpdatedDate$();
    this.chatNotificationCount$ = chatNotificationService.getRoomNotificationCount$(this._sabhaRoom.id);
  }

  public getSabhaDeviceParticipant$(): Observable<SabhaUserEntity | null> {
    return this.userRepository.getParticipants$(this.participants.map(p => p.userId)).pipe(
      take(1),
      switchMap((participants: SabhaUserEntity[]) => {
        const deviceParticipantIndex = participants.findIndex(participant => participant && !!participant.externalId);
        return of(deviceParticipantIndex > -1 ? participants[deviceParticipantIndex] : null);
      })
    );
  }

  public getRemoteParticipants$(): Observable<SabhaUserEntity[]> {
    return this.userRepository.getParticipants$(this.participants.map(p => p.userId)).pipe(
      take(1),
      switchMap((ps: SabhaUserEntity[]) => {
        return of(ps.filter(p => p.userId !== this.userRepository.currentUserId));
      })
    );
  }

  public dispose() {
    console.log('Disposing chat room ' + this.id);
    this._subscriptions?.unsubscribe();
    this._callDurationSubscription?.unsubscribe();
  }

  private onCallEnd(): void {
    this._callDuration$ = null;
    this._callDurationSubscription?.unsubscribe();
  }

  private setupCallDuration(): void {
    this._callDuration$ = new BehaviorSubject(0);
    this._callDurationSubscription = new Subscription();
    this._callDurationSubscription.add(this.getCallDuration$().subscribe(this._callDuration$));
  }

  private getCallDuration$(): Observable<number> {
    const onDestroy$ = this.callContext$.pipe(
      filter(c => !!c),
      switchMap(c => c.onDestroy$)
    );
    const joinTime$ = this.callContext$.pipe(
      filter(c => !!c),
      takeUntil(onDestroy$),
      switchMap(c => c.participants$),
      filter(p => p.findIndex(p => !p.isLocal) > -1),
      map(participants => participants.find(p => !p.isLocal).joinTime),
      first()
    );

    const secondInMs = 1000;
    const interval$ = interval(secondInMs).pipe(takeUntil(onDestroy$));
    return combineLatest([joinTime$, interval$]).pipe(
      takeUntil(onDestroy$),
      map(([joinTime, _]) => Date.now() - joinTime)
    );
  }

  private getLastUpdatedDate$(): Observable<number> {
    return combineLatest([this.callContext$, this.chatContext$]).pipe(
      switchMap(([call, chat]) => combineLatest([of(call), of(chat), chat?.lastUpdatedTime$ ?? of(0)])),
      map(([call, chat, chatLastUpdated]) => {
        const roomCreationDate = new Date(this.creationTime).getTime();
        if (!call && !chat) {
          return roomCreationDate;
        }

        if (!call) {
          return roomCreationDate > chat.lastUpdatedTime ? roomCreationDate : chat.lastUpdatedTime;
        }

        if (!chat) {
          return roomCreationDate > call.lastUpdatedTime ? roomCreationDate : call.lastUpdatedTime;
        }

        return call.lastUpdatedTime > chatLastUpdated ? call.lastUpdatedTime : chatLastUpdated;
      })
    ) as Observable<number>;
  }
}
